import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const lineLabel = (l) => [l.size, l.color].filter(Boolean).join(" / ") || l.product_name || "Standart";
const remOf = (l) => Math.max(0, (l.overcut_qty || 0) - (l.stocked_qty || 0));

// Fazla kesim mamulü — beden bazında kısmi stoğa alma, hedef depo zorunlu
export function OvercutStockDialog({ job, onClose, onDone }) {
  const lines = useMemo(() => (job?.cutting?.lines || []).map((l, i) => ({ ...l, idx: String(i) })).filter((l) => (l.overcut_qty || 0) > 0), [job]);
  const [whs, setWhs] = useState([]);
  const [wh, setWh] = useState("");
  const [qty, setQty] = useState({});
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!job) return;
    setQty(Object.fromEntries(lines.map((l) => [l.idx, remOf(l)])));
    setKey(crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    api.get("/warehouses").then(({ data }) => {
      const list = (Array.isArray(data) ? data : data?.items || []).filter((w) => w.is_active !== false);
      setWhs(list); setWh(list.length === 1 ? list[0].id : "");
    }).catch(() => setWhs([]));
  }, [job, lines]);

  const total = Object.values(qty).reduce((a, b) => a + (Number(b) || 0), 0);
  const submit = async () => {
    setBusy(true);
    try {
      const quantities = Object.fromEntries(Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post(`/departments/jobs/${job.id}/cutting/add-to-stock`, { warehouse_id: wh, quantities, request_id: key });
      toast.success(data.duplicate ? "Bu giriş zaten kaydedilmişti" : `${total} adet fazla mamul stoğa alındı${data.completed ? " (tamamlandı)" : ""}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md" data-testid="overcut-stock-dialog">
        <DialogHeader><DialogTitle>Fazla Mamulü Stoğa Al · {job?.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {lines.map((l) => {
            const rem = remOf(l);
            return (
              <div key={l.idx} className="flex items-center gap-3 text-sm" data-testid={`overcut-stock-line-${l.idx}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{lineLabel(l)}</div>
                  <div className="text-[11px] text-slate-500 font-mono" data-testid={`overcut-stock-summary-${l.idx}`}>
                    Fazla Üretim {l.overcut_qty} · Daha Önce Stoğa Alınan {l.stocked_qty || 0} · Kalan {rem}
                  </div>
                </div>
                <Input type="number" min={0} max={rem} disabled={!rem} value={qty[l.idx] ?? 0} aria-label="Bu İşlemde Stoka Alınacak"
                  onChange={(e) => setQty((q) => ({ ...q, [l.idx]: Math.min(rem, Math.max(0, parseInt(e.target.value || "0", 10))) }))}
                  className="w-24 h-9 bg-slate-950 border-slate-800 rounded-sm text-right" data-testid={`overcut-stock-qty-${l.idx}`} />
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-slate-500 text-right">Bu İşlemde Stoka Alınacak: <span className="text-slate-200 font-mono" data-testid="overcut-stock-total">{total}</span></div>
        <Select value={wh} onValueChange={setWh}>
          <SelectTrigger className="bg-slate-950 border-slate-800 rounded-sm" data-testid="overcut-stock-warehouse"><SelectValue placeholder="Hedef depo seçin" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            {whs.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm border-slate-700" onClick={onClose}>Vazgeç</Button>
          <Button className="rounded-sm bg-cyan-600 hover:bg-cyan-500" disabled={!wh || !total || busy} onClick={submit} data-testid="overcut-stock-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `${total} Adet Stoğa Al`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
